// PerStoreHistoryModal — per-store run history + score-over-time sparkline.
//
// Opened from clicking a store row in RegistryTable. Cloudscape `<Modal>`
// with three sections stacked: header card with current score + version
// badge, sparkline of score-over-time (inline SVG, no charting lib for v1),
// and a run-history table. Clicking a history row swaps the modal content
// to PerRunDetailsView.
//
// Δ-vs-prev cell shape per decision #19: "+2.0 pts (+3.1%)", green / red /
// grey based on direction. Decision #21 surfaces a yellow version-mismatch
// badge when the run was computed under a different scoring version.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Button,
  Header,
  SpaceBetween,
  Table,
  StatusIndicator,
} from '@cloudscape-design/components';

import PerRunDetailsView from './PerRunDetailsView.jsx';
import { storeCROAuditApi } from './storeCROAuditApi.js';

// Current scoring version — keep in sync with the Lambda's config.
// Change-detect when bumping (decision #21).
const CURRENT_SCORING_VERSION = '1.2.0';

export default function PerStoreHistoryModal({
  visible,
  shopId,
  onDismiss,
  onError,
  onReauditDone,
  username,
}) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRun, setActiveRun] = useState(null);
  const [reauditing, setReauditing] = useState(false);

  useEffect(() => {
    if (!visible || !shopId) return;
    let cancelled = false;
    setLoading(true);
    setActiveRun(null);
    storeCROAuditApi
      .getStoreHistory(shopId)
      .then((data) => {
        if (cancelled) return;
        setHistory(data?.runs || []);
      })
      .catch((err) => {
        onError?.(err.message || 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, shopId]); // eslint-disable-line react-hooks/exhaustive-deps

  // For each row in the history table we need the run's `total_score` /
  // `score_percentage` to render. We fetch each run JSON lazily on
  // demand — for v1 we eagerly hydrate the most recent 10 to keep the
  // sparkline cheap; older rows show "—" until clicked.
  const [hydrated, setHydrated] = useState({}); // { runFilename: blob }
  useEffect(() => {
    if (!history.length) return;
    let cancelled = false;
    const recent = history.slice(0, 10);
    Promise.all(
      recent.map((entry) =>
        storeCROAuditApi
          .getRunDetails(shopId, entry.key)
          .then((blob) => ({ key: entry.key, blob }))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      const next = {};
      for (const r of results) {
        if (r) next[r.key] = r.blob;
      }
      setHydrated((prev) => ({ ...prev, ...next }));
    });
    return () => {
      cancelled = true;
    };
  }, [history, shopId]);

  // Build sparkline series newest-first → oldest-first (left to right
  // makes more sense for a trend chart).
  const sparklinePoints = useMemo(() => {
    const points = [];
    for (let i = history.length - 1; i >= 0; i--) {
      const blob = hydrated[history[i].key];
      if (blob && typeof blob.score_percentage === 'number') {
        points.push(blob.score_percentage);
      }
    }
    return points;
  }, [history, hydrated]);

  const latestBlob = history.length > 0 ? hydrated[history[0].key] : null;

  const handleReaudit = async () => {
    if (!latestBlob || !latestBlob.store_uri) return;
    setReauditing(true);
    try {
      await storeCROAuditApi.submitSingleStore({
        shop_id: shopId,
        store_url: latestBlob.store_uri,
        settings: {},
      });
      onReauditDone?.();
    } catch (err) {
      onError?.(err.message || 'Re-audit submit failed');
    } finally {
      setReauditing(false);
    }
  };

  // When `activeRun` is set, swap modal body to PerRunDetailsView.
  if (activeRun) {
    return (
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        size="max"
        closeAriaLabel="Close run details"
        header={
          <Box>
            <Button iconName="angle-left" variant="link" onClick={() => setActiveRun(null)}>
              Back to history
            </Button>
            <Header variant="h2">
              {latestBlob?.store_name || 'Store'} · run{' '}
              {formatTimestamp(activeRun.timestamp)}
            </Header>
          </Box>
        }
      >
        <PerRunDetailsView run={activeRun.blob} />
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      closeAriaLabel="Close history"
      header={
        <Header
          variant="h2"
          description={
            latestBlob?.store_uri
              ? `${latestBlob.store_uri} · shop_id ${shopId}`
              : `shop_id ${shopId}`
          }
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                iconName="refresh"
                onClick={handleReaudit}
                loading={reauditing}
                disabled={!latestBlob}
              >
                Re-audit now
              </Button>
            </SpaceBetween>
          }
        >
          {latestBlob?.store_name || `shop_id ${shopId}`}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {/* Current score header */}
        {latestBlob && (
          <Box variant="awsui-key-label">
            <SpaceBetween direction="horizontal" size="m">
              <span className={`cro-audit-score-pill ${bandFor(latestBlob.score_percentage)}`}>
                {Number(latestBlob.total_score || 0).toFixed(1)} /{' '}
                {Number(latestBlob.max_possible_score || 0).toFixed(1)} (
                {Number(latestBlob.score_percentage || 0).toFixed(1)}%)
              </span>
              <span style={{ color: '#64748b' }}>
                Last run: {formatTimestamp(history[0]?.timestamp)} ·{' '}
                {history[0]?.trigger_kind || ''}
                {history[0]?.trigger_username && ` · ${history[0].trigger_username}`}
              </span>
              {latestBlob.scoring_version &&
                latestBlob.scoring_version !== CURRENT_SCORING_VERSION && (
                  <span
                    className="cro-audit-version-badge"
                    title={`Computed under v${latestBlob.scoring_version}. Current scorer is v${CURRENT_SCORING_VERSION}. Scores not directly comparable.`}
                  >
                    ⚠ v{latestBlob.scoring_version}
                  </span>
                )}
            </SpaceBetween>
          </Box>
        )}

        {/* Sparkline */}
        {sparklinePoints.length >= 2 && (
          <Box>
            <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>
              Score over time
            </Box>
            <Sparkline points={sparklinePoints} />
          </Box>
        )}

        {/* Run history table */}
        <Table
          variant="embedded"
          loading={loading}
          loadingText="Loading history..."
          items={history}
          columnDefinitions={[
            {
              id: 'when',
              header: 'Date',
              cell: (item) => formatTimestamp(item.timestamp),
              width: 160,
            },
            {
              id: 'trigger',
              header: 'Trigger',
              cell: (item) =>
                item.trigger_kind === 'scheduled' ? (
                  <StatusIndicator type="info">scheduled</StatusIndicator>
                ) : (
                  <StatusIndicator type="pending" colorOverride="grey">
                    ↑ {item.trigger_username || 'unknown'}
                  </StatusIndicator>
                ),
              width: 160,
            },
            {
              id: 'score',
              header: 'Score',
              cell: (item) => {
                const blob = hydrated[item.key];
                if (!blob) return '—';
                if (blob.status === 'failed') {
                  return (
                    <SpaceBetween size="xxs">
                      <StatusIndicator type="error">Failed</StatusIndicator>
                      <Box variant="small" color="text-status-error">
                        {(blob.error || 'Unknown error').slice(0, 80)}
                        {(blob.error || '').length > 80 ? '…' : ''}
                      </Box>
                    </SpaceBetween>
                  );
                }
                return (
                  <span className={`cro-audit-score-pill ${bandFor(blob.score_percentage)}`}>
                    {Number(blob.total_score || 0).toFixed(1)} (
                    {Number(blob.score_percentage || 0).toFixed(1)}%)
                  </span>
                );
              },
              width: 180,
            },
            {
              id: 'delta',
              header: 'Δ vs prev',
              cell: (item, idx) => deltaCell(history, hydrated, item, idx),
              width: 160,
            },
            {
              id: 'view',
              header: '',
              cell: (item) => {
                const blob = hydrated[item.key];
                return (
                  <Button
                    variant="inline-link"
                    disabled={!blob}
                    onClick={() => setActiveRun({ timestamp: item.timestamp, blob })}
                  >
                    View this run
                  </Button>
                );
              },
              width: 130,
            },
          ]}
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              <SpaceBetween size="xxs">
                <Box variant="p">
                  <b>No runs yet</b>
                </Box>
                <Box color="text-body-secondary">
                  This store will appear here after its first audit completes.
                </Box>
              </SpaceBetween>
            </Box>
          }
        />
      </SpaceBetween>
    </Modal>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function bandFor(percentage) {
  const pct = Number(percentage || 0);
  if (pct >= 80) return 'green';
  if (pct >= 50) return 'yellow';
  if (pct > 0) return 'red';
  return 'grey';
}

function formatTimestamp(value) {
  if (!value) return '—';
  const normalized = String(value).replace(/T(\d{2})-(\d{2})-(\d{2})Z$/, 'T$1:$2:$3Z');
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return value;
  return (
    dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ' · ' +
    dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  );
}

function deltaCell(history, hydrated, item, idx) {
  // Δ vs the next-older run (history is newest-first, so prev = idx + 1).
  // Format: "+2.5 pts (+3.8%)" per decision #19.
  const blob = hydrated[item.key];
  const prev = history[idx + 1];
  const prevBlob = prev ? hydrated[prev.key] : null;

  if (!blob) return '—';
  if (!prevBlob) return <span className="cro-audit-delta flat">first run</span>;

  const deltaPts = Number(blob.total_score || 0) - Number(prevBlob.total_score || 0);
  const deltaPct =
    Number(blob.score_percentage || 0) - Number(prevBlob.score_percentage || 0);

  let cls = 'flat';
  if (deltaPts > 0.05) cls = 'up';
  else if (deltaPts < -0.05) cls = 'down';

  const ptsLabel = deltaPts >= 0 ? `+${deltaPts.toFixed(1)}` : deltaPts.toFixed(1);
  const pctLabel = deltaPct >= 0 ? `+${deltaPct.toFixed(1)}` : deltaPct.toFixed(1);

  return (
    <span className={`cro-audit-delta ${cls}`}>
      {ptsLabel} pts ({pctLabel}%)
    </span>
  );
}

function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const width = 600;
  const height = 60;
  const padding = 4;
  const minVal = 0;
  const maxVal = 100;
  const span = Math.max(maxVal - minVal, 1);
  const stepX = (width - padding * 2) / (points.length - 1);

  const coords = points.map((v, i) => {
    const x = padding + stepX * i;
    const y = padding + (height - padding * 2) * (1 - (v - minVal) / span);
    return { x, y };
  });
  const d = coords
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');

  return (
    <svg
      className="cro-audit-trend-sparkline"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
    >
      <path d={d} />
      {coords.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={2.5} />
      ))}
    </svg>
  );
}
